/**
 * Componente DealKanbanColumn - Coluna do Kanban de Deals
 * Carrega deals internamente com cursor-based pagination
 * Suporta reordenação de colunas via drag & drop
 * Versão: 2.4.0 - Rolagem infinita contínua (InfiniteScrollSentinel), sem o
 *                 botão "Carregar mais" — mesmo comportamento do Kanban de
 *                 Tarefas. Nenhuma mudança na busca de dados/filtros/DnD.
 * Versão: 2.2.1 - Removido overflow-hidden do wrapper droppable para permitir
 *                 que o hover:shadow-md dos cards renderize dentro do padding
 *                 do ScrollArea (8px) sem ser recortado nas bordas. Antes,
 *                 a sombra ficava colada na borda do card e, em colunas com
 *                 cor de fase neutra/cinza (ex: "Lead novo"), aparecia como
 *                 se o card estivesse "estourando" ou cortado.
 * Versão: 2.2.0 - ScrollArea com type="always" para evitar overflow horizontal
 */
import React, { useMemo, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus, Loader2, GripVertical, Zap, Pencil } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { DealColumnExportButton } from './DealColumnExportButton';
import { cn } from '@/lib/utils';
import { DealCard } from './DealCard';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { InfiniteScrollSentinel } from '@/components/ui/infinite-scroll-sentinel';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { useKanbanColumn, KanbanDeal } from '@/hooks/useKanbanColumn';
import { useContactsUnreadSummary, UnreadSummaryData } from '@/hooks/useContactsUnreadSummary';
import { CrmDeal, DealFilters } from '@/hooks/useCrmDeals';
import { ColumnSortDialog, ColumnSortConfig, getColumnSortConfig } from './ColumnSortDialog';


/**
 * Converte cor hex para rgba com opacidade
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Props do componente
interface DealKanbanColumnProps {
  pipelineId: string;
  phaseId: string;
  phaseName: string;
  phaseColor: string | null;
  dealCount: number;
  totalValue: number;
  isWon?: boolean;
  isLost?: boolean;
  filters?: DealFilters;
  onAddDeal?: (phaseId: string) => void;
  onEditDeal?: (deal: CrmDeal) => void;
  onViewDeal?: (deal: CrmDeal) => void;
  onMarkWon?: (deal: CrmDeal) => void;
  onMarkLost?: (deal: CrmDeal) => void;
  onReopen?: (deal: CrmDeal) => void;
  /** Indica se a fase tem automações configuradas */
  hasAutomation?: boolean;
  /** Quantidade de automações na fase */
  automationCount?: number;
  /** Callback ao clicar no ícone de automação */
  onAutomationClick?: () => void;
  /** Map de resumo de não lidas por contato */
  unreadSummaryMap?: Map<string, UnreadSummaryData>;
  /** Map de atualizações Realtime de unread (vem do Board centralizado) */
  realtimeUnreadUpdates?: Map<string, UnreadSummaryData>;
  /** Callback para abrir chat de uma conversa */
  onOpenChat?: (conversationId: string) => void;
  /** Callback para abrir modal de cliente */
  onViewCustomer?: (customerId: string) => void;
  /** Callback para abrir modal 360º do contato (quando não há cliente).
      v1.9.0: repassa o preview do contato junto ao id */
  onViewContact?: (contactId: string, preview?: {
    id: string;
    name?: string | null;
    phone?: string | null;
    email?: string | null;
    avatar_url?: string | null;
  }) => void;
  /**
   * Permissão granular `can_change_phase` para esta fase específica.
   * Quando `false`, oculta o handle de arrasto dos cards e desabilita `useSortable`
   * no `DealCard`. Default `true` mantém compatibilidade com o comportamento legado.
   */
  canDrag?: boolean;
  /** v2.5.0: Callback para abrir modal de atividade ao clicar no badge de checklist */
  onOpenActivity?: (dealId: string) => void;
  /**
   * Permissões granulares por fase (workspace-scoped) repassadas ao DealCard.
   * Default `true` preserva o comportamento legado quando o granular está OFF.
   */
  canEditDeal?: boolean;
  canChangePhase?: boolean;
  canReopenDeal?: boolean;
}

/**
 * Converte KanbanDeal para CrmDeal para compatibilidade
 */
/**
 * Converte KanbanDeal para CrmDeal para compatibilidade
 * v1.4.0: Adicionado mapeamento de source, probability e contact.phone
 */
function convertToCrmDeal(kanbanDeal: KanbanDeal, phaseId: string, pipelineId: string): CrmDeal {
  return {
    id: kanbanDeal.id,
    title: kanbanDeal.title,
    company_name: kanbanDeal.company_name,
    description: kanbanDeal.description || null,
    value: kanbanDeal.value,
    currency: kanbanDeal.currency,
    // v1.4.0: Mapeia probability da RPC
    probability: kanbanDeal.probability,
    expected_close_date: kanbanDeal.expected_close_date,
    actual_close_date: null,
    loss_reason: null,
    // v1.4.0: Mapeia source da RPC
    source: kanbanDeal.source,
    custom_fields: {},
    metadata: {},
    created_by: null,
    phase_id: phaseId,
    pipeline_id: pipelineId,
    // v1.5.0: Mapeia tenant_id da RPC para corrigir bug de tags
    tenant_id: kanbanDeal.tenant_id,
    // v1.6.0: Usa status real do deal ao invés de hardcoded 'open'
    status: kanbanDeal.status as CrmDeal['status'],
    priority: kanbanDeal.priority as CrmDeal['priority'],
    sort_order: 0,
    created_at: kanbanDeal.created_at,
    updated_at: kanbanDeal.updated_at,
    contact_id: kanbanDeal.contact_id,
    assigned_to: kanbanDeal.assigned_to,
    seller_id: kanbanDeal.seller_id,
    // v1.4.0: Mapeia contact.phone da RPC
    contact: kanbanDeal.contact_name ? {
      id: kanbanDeal.contact_id!,
      name: kanbanDeal.contact_name,
      phone: kanbanDeal.contact_phone,
      email: null,
      avatar_url: kanbanDeal.contact_avatar,
    } : null,
    assigned_user: kanbanDeal.assigned_user_name ? {
      id: kanbanDeal.assigned_to!,
      full_name: kanbanDeal.assigned_user_name,
      avatar_url: kanbanDeal.assigned_user_avatar,
    } : null,
    // v1.3.0: Mapeia dados do vendedor
    seller: kanbanDeal.seller_name ? {
      id: kanbanDeal.seller_id!,
      name: kanbanDeal.seller_name,
      email: null,
      phone: null,
      avatar_url: kanbanDeal.seller_avatar,
    } : null,
    // v1.9.0: Mapeia último contrato do deal
    last_contract_status: kanbanDeal.last_contract_status,
    last_contract_title: kanbanDeal.last_contract_title,
    // v2.0.0: customer_id para abrir modal de cliente
    customer_id: kanbanDeal.customer_id,
    // v2.3.0: Data de atualização da fase
    phase_updated_at: kanbanDeal.phase_updated_at,
    // v2.5.0: Repassa agregação de cadência (badge no card sem N+1)
    cadence_phase_total: kanbanDeal.cadence_phase_total,
    cadence_phase_pending: kanbanDeal.cadence_phase_pending,
    cadence_phase_has_overdue: kanbanDeal.cadence_phase_has_overdue,
  };
}

/**
 * Componente de Coluna do Kanban com carregamento interno
 * Suporta drag & drop para reordenação de colunas
 */
export function DealKanbanColumn({
  pipelineId,
  phaseId,
  phaseName,
  phaseColor,
  dealCount,
  totalValue,
  isWon = false,
  isLost = false,
  filters,
  onAddDeal,
  onEditDeal,
  onViewDeal,
  onMarkWon,
  onMarkLost,
  onReopen,
  hasAutomation = false,
  automationCount = 0,
  onAutomationClick,
  unreadSummaryMap,
  realtimeUnreadUpdates,
  onOpenChat,
  onViewCustomer,
  onViewContact,
  canDrag = true,
  onOpenActivity,
  canEditDeal = true,
  canChangePhase = true,
  canReopenDeal = true,
}: DealKanbanColumnProps) {
  // v1.6.0: Permissões para exportação
  const { isAdmin, canView } = usePermissions();
  const canExport = isAdmin || canView('crm_deals_export');

  // v2.3.0: Estado de ordenação da coluna (persistido em localStorage)
  const [sortConfig, setSortConfig] = useState<ColumnSortConfig>(() => getColumnSortConfig(phaseId));
  const [sortDialogOpen, setSortDialogOpen] = useState(false);

  // Carrega deals desta coluna com cursor-based pagination, filtros e ordenação
  const { 
    deals: kanbanDeals, 
    isLoading, 
    isFetchingNextPage, 
    hasNextPage, 
    fetchNextPage 
  } = useKanbanColumn(pipelineId, phaseId, filters, sortConfig.sortField, sortConfig.sortDirection);

  // Converte para formato CrmDeal para compatibilidade
  const deals = kanbanDeals.map(d => convertToCrmDeal(d, phaseId, pipelineId));

  // Cria Map de tags por deal_id para lookup O(1) — tags já vêm da RPC inline
  const dealTagsMap = useMemo(() => {
    const map = new Map<string, Array<{ deal_id: string; tag_id: string; assigned_by: null; assigned_at: string; tag_name: string; tag_color: string }>>();
    kanbanDeals.forEach(kd => {
      map.set(kd.id, (kd.tags || []).map(t => ({
        deal_id: kd.id,
        tag_id: t.tag_id,
        assigned_by: null,
        assigned_at: '',
        tag_name: t.tag_name,
        tag_color: t.tag_color,
      })));
    });
    return map;
  }, [kanbanDeals]);

  // Extrai contact_ids dos deals carregados para buscar resumo de não lidas
  const contactIds = useMemo(
    () => deals.map(d => d.contact_id).filter((id): id is string => !!id),
    [deals]
  );

  // Hook batch: busca contagem de não lidas + última conversa de todos os contatos da coluna
  const { summaryMap: localUnreadMap } = useContactsUnreadSummary(contactIds, deals.length > 0);

  // Mescla: RPC inicial + Realtime updates do Board centralizado
  // A prop unreadSummaryMap serve como override total; caso contrário, mescla RPC + Realtime
  const effectiveUnreadMap = useMemo(() => {
    // Se prop override externa foi passada, usa diretamente
    if (unreadSummaryMap) return unreadSummaryMap;
    // Se não há atualizações Realtime, retorna Map da RPC
    if (!realtimeUnreadUpdates || realtimeUnreadUpdates.size === 0) return localUnreadMap;
    // Mescla: RPC como base, Realtime sobrescreve valores atualizados
    const merged = new Map(localUnreadMap);
    realtimeUnreadUpdates.forEach((value, key) => {
      // Só aplica se o contact_id está nos deals desta coluna
      if (contactIds.includes(key)) {
        merged.set(key, value);
      }
    });
    return merged;
  }, [unreadSummaryMap, localUnreadMap, realtimeUnreadUpdates, contactIds]);

  // Configuração do sortable para reordenação de colunas
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `column-${phaseId}`,
    data: {
      type: 'column',
      phaseId,
    },
  });

  // Configuração do droppable para receber deals
  // IMPORTANTE: O data com type e phaseId permite identificar a fase de destino no handleDragEnd
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: phaseId,
    data: {
      type: 'phase',
      phaseId,
    },
  });

  // IDs dos deals para o SortableContext
  const dealIds = deals.map(d => d.id);

  // Estilo de transformação para drag & drop de colunas
  // Transição suave para colunas vizinhas deslizarem ao reordenar
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
  };

  return (
    <div
      ref={setSortableRef}
      style={style}
      className={cn(
        'flex flex-col w-[300px] h-full shrink-0 bg-muted/30 rounded-lg overflow-hidden transition-all duration-200',
        // Visual elegante durante hover de drop: borda suave + fundo sutil
        isOver && 'ring-2 ring-primary/40 bg-primary/5 scale-[1.01]',
        // Placeholder invisível durante arrasto (DragOverlay mostra a cópia)
        isDragging && 'opacity-0'
      )}
    >
      {/* Header da coluna - Com fundo colorido sutil e handle de arrasto */}
      <div 
        className="p-3 border-b border-border/50 rounded-t-lg group"
        style={{ backgroundColor: hexToRgba(phaseColor || '#6b7280', 0.15) }}
      >
        {/* Nome da fase com indicador de cor e handle de arrasto */}
        <div className="flex items-center gap-2 mb-2">
          {/* Handle de arrasto - aparece no hover */}
          <button
            {...attributes}
            {...listeners}
            className={cn(
              "p-0.5 rounded cursor-grab active:cursor-grabbing",
              "opacity-0 group-hover:opacity-100 transition-opacity",
              "hover:bg-muted/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
            )}
            title="Arraste para reordenar"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
          
          <div
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: phaseColor || '#6b7280' }}
          />
          <h3 className="font-medium text-sm truncate flex-1">
            {phaseName}
          </h3>
          <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
            {dealCount}
          </span>
        </div>

        {/* Valor total da coluna */}
        <div className="flex items-center justify-between">
          <PriceDisplay amount={totalValue} size="sm" />
          
          <div className="flex items-center gap-0.5">
            {/* v1.6.0: Botão de exportação (CSV/XLS) — condicional por permissão */}
            {canExport && (
              <DealColumnExportButton
                pipelineId={pipelineId}
                phaseId={phaseId}
                phaseName={phaseName}
                filters={filters}
                sortField={sortConfig.sortField}
                sortDirection={sortConfig.sortDirection}
              />
            )}

            {/* v2.3.0: Botão para configurar ordenação da coluna */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSortDialogOpen(true);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  Configurar coluna
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Ícone de automação (se fase tem automação configurada) */}
            {hasAutomation && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-amber-500 hover:text-amber-600 hover:bg-amber-500/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAutomationClick?.();
                      }}
                    >
                      <Zap className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {automationCount} automação{automationCount !== 1 ? 'ões' : ''} ativa{automationCount !== 1 ? 's' : ''}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            {/* Botão adicionar (não mostra em colunas de ganho/perda) */}
            {!isWon && !isLost && onAddDeal && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => onAddDeal(phaseId)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 
        Área de cards com scroll interno por coluna
        - h-0 min-h-0 = força o flex-1 a respeitar altura do container
        - preventScrollChaining = impede que scroll "escape" para a página
          quando o usuário chega no topo/rodapé da lista de cards
      */}
      {/* v2.5.0: p-2 no ScrollArea (padrão do Kanban de Conversas) evita que scrollbar sobreponha cards */}
      <ScrollArea className="flex-1 h-0 min-h-0 p-2" preventScrollChaining touchAxis="pan-y" axis="y">
        <div
          ref={setDroppableRef}
          /*
           * v2.2.1: Removido `overflow-hidden` daqui.
           * O padding do ScrollArea (p-2 = 8px) já garante respiro horizontal
           * para a sombra hover do card. Manter `overflow-hidden` aqui
           * recortava a sombra exatamente na borda do card, criando a
           * impressão visual de que o card estava cortado/estourando a
           * coluna — efeito mais perceptível na primeira fase (cor neutra).
           */
          className="w-full space-y-2 min-h-[100px]"
        >
          {/* Loading inicial */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Lista de deals */}
          {!isLoading && (
            <SortableContext items={dealIds} strategy={verticalListSortingStrategy}>
              {deals.map((deal) => {
                // Busca dados de não lidas do contato no Map (O(1))
                const contactSummary = deal.contact_id
                  ? effectiveUnreadMap?.get(deal.contact_id)
                  : undefined;

                return (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    sortField={sortConfig.sortField}
                    onEdit={onEditDeal}
                    onView={onViewDeal}
                    onMarkWon={onMarkWon}
                    onMarkLost={onMarkLost}
                    onReopen={onReopen}
                    unreadCount={contactSummary?.unreadCount}
                    lastConversationId={contactSummary?.lastConversationId}
                    onOpenChat={onOpenChat}
                    onViewCustomer={onViewCustomer}
                    onViewContact={onViewContact}
                    batchTagAssignments={dealTagsMap.get(deal.id) ?? []}
                    canDrag={canDrag}
                    onOpenActivity={onOpenActivity}
                    canEditDeal={canEditDeal}
                    canChangePhase={canChangePhase}
                    canReopenDeal={canReopenDeal}
                  />
                );
              })}
            </SortableContext>
          )}

          {/* Placeholder quando vazio */}
          {!isLoading && deals.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <p className="text-sm">Nenhum negócio</p>
              {!isWon && !isLost && onAddDeal && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => onAddDeal(phaseId)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              )}
            </div>
          )}

          {/* v2.3.0 -> v2.4.0: Rolagem infinita sem botão "Carregar mais"
              (paridade com o Kanban de Tarefas). A sentinela dispara a próxima
              página ao entrar no viewport da coluna. */}
          {!isLoading && (
            <InfiniteScrollSentinel
              hasMore={!!hasNextPage}
              isLoading={isFetchingNextPage}
              onLoadMore={fetchNextPage}
            />
          )}

        </div>
      </ScrollArea>

      {/* v2.3.0: Dialog de configuração de ordenação */}
      {sortDialogOpen && (
        <ColumnSortDialog
          open={sortDialogOpen}
          onOpenChange={setSortDialogOpen}
          phaseId={phaseId}
          phaseName={phaseName}
          phaseColor={phaseColor}
          currentConfig={sortConfig}
          onSave={setSortConfig}
        />
      )}
    </div>
  );
}
